import { computed, action, observable } from 'mobx';
import { events, emit } from 'elc-service-bus';
import { root } from 'elc-universal-utils';
import { buildIngredientsContent } from '../utils/ContentFormatter';
import { BroadcastProductsLoadedUseCase } from '../use-cases/BroadcastProductsLoadedUseCase';
import { ProductLoadedUseCase } from '../use-cases/ProductLoadedUseCase';
import { IAsset } from '../domain/entities/Asset';
import { ProductBaseController } from './ProductBaseController';
import {
    IProductFullOrderable,
    IProductFullConfig,
    IProductDetails,
    IProductFullOrderableViewControllerData
} from '../../exported/product-full-order/IProductFullOrderable';
import {
    ProductInfoPopupFieldType,
    IProductInfoPopupPayload
} from '../constants/ProductInfoPopupFieldType';
import { truncateStringToNearestWord } from '../utils/truncateStringToNearestWord';
import { PopulateProductFromServerState } from '../use-cases/PopulateProductFromServerState';
import { STICKY_CLASS_NAMES } from '../constants/StickyAddToBag';
import { mergeConfigs } from '../utils/ConfigUtils';
import { ProductRepository } from '../repositories/ProductRepository';
import { diContainer, serviceNames } from '../../service-setup/diContainer';
import { InventoryStatusSortingStrategy } from '../domain/services/InventoryStatusSortingStrategy';

export class ProductFullOrderableViewController extends ProductBaseController {
    @observable public isPreloaded: boolean = false;
    @observable public isLoaded: boolean = false;
    @observable public addingToBag: boolean = false;
    // if the add to bag fails in the first instance, will persist the disabled state of the button
    @observable public disableAddToCart: boolean = false;
    @observable public showStickyAddToBag: boolean = false;
    @observable public isShowingSuccessLabel: boolean = false;
    @observable public hideProductImageSection: boolean = false;
    @observable public isDescriptionExpanded: boolean = false;
    @observable public isPerfectShadeEnabled: boolean = false;
    @observable public isProductInfoPopupOpen: boolean = false;
    @observable public productInfoPopupFieldType: string;
    @observable public productInfoPopupHeader?: string;
    @observable public currentTabIndex: string = '0';
    @observable private config: IProductFullConfig;
    @observable protected productRepository: ProductRepository;
    public dataState: IProductFullOrderableViewControllerData;
    public configMergeStrategy: Function = mergeConfigs;

    constructor() {
        super();

        this.productRepository = diContainer.get(serviceNames.productRepository);
    }

    @action
    public loaded = async (props: IProductFullOrderable) => {
        this.config = props.config;
        const { productId, inventoryStatusSorting } = this.config;

        await new ProductLoadedUseCase(productId).execute();

        const product = this.productRepository.getProductById(productId);

        if (!!product) {
            this.setProduct(product);

            const isBackorderEnabled = this.config.backorder && this.config.backorder.enabled;
            if (inventoryStatusSorting && inventoryStatusSorting.enabled) {
                const sortingStrategy = new InventoryStatusSortingStrategy(
                    inventoryStatusSorting.statuses || [],
                    isBackorderEnabled
                );

                this.product.sortSkus(sortingStrategy.sortSkusByInventory);
            }

            this.setSelectedSkuId(this.product.defaultSku.skuId);
            this.checkUrlParam();

            this.isLoaded = true;

            const broadcastProductLoadedUseCase = new BroadcastProductsLoadedUseCase([productId]);
            await broadcastProductLoadedUseCase.execute();
        }
    };

    @action
    public handleState = (config: IProductFullConfig) => {
        if (!this.dataState) {
            return;
        }
        try {
            this.config = config;

            new PopulateProductFromServerState(this.dataState.product).execute();

            const product = this.productRepository.getProductById(config.productId);
            if (!!product) {
                this.setProduct(product);
                this.checkUrlParam();

                this.isLoaded = true;
            }
        } catch (error) {
            console.warn(error);
        }
    };

    @computed
    get data(): IProductFullOrderableViewControllerData {
        return {
            selectedSku: this.selectedSku,
            product: this.product,
            largeImages: this.largeImages,
            smallImages: this.smallImages,
            howDoesItWorkLabel: this.product.howDoesItWork.label,
            selectedAutoReplenishOption: this.selectedAutoReplenishOption,
            isAutoReplenishSelected: this.isAutoReplenishSelected,
            modelAndSmooshImages: this.modelAndSmooshImages,
            hideProductImageSection: this.hideProductImageSection,
            isVtoMakeupEnabled: this.vtoMakeupEnabled && this.vtoMakeupFeatureEnabled,
            isVtoFoundationEnabled: this.vtoFoundationEnabled && this.vtoFoundationFeatureEnabled,
            isPerfectShadeEnabled: this.isPerfectShadeEnabled,
            engravingEnabled: this.engravingEnabled,
            favoritesEnabled: this.favoritesEnabled,
            isLoaded: this.isLoaded,
            stickyAddToBagVisibleClassName: this.stickyAddToBagVisibleClassName,
            showStickyAddToBag: this.showStickyAddToBag,
            isDescriptionExpanded: this.isDescriptionExpanded,
            charLimit: this.charLimit,
            productTruncatedDescription: this.productTruncatedDescription,
            productInfoPopupFieldType: this.productInfoPopupFieldType,
            productInfoPopupHeader: this.productInfoPopupHeader,
            productInfoPopupData: this.productInfoPopupData,
            currentTabId: this.currentTabId,
            ...this.productDetails
        };
    }

    public emitReviewsInit = (productId: string) => {
        emit(events.SPP_POWER_REVIEWS_INIT, productId);
    };

    public emitProductFullLoaded = (productId: string) => {
        emit(events.PRODUCT_FULL_LOADED, { productId, skuId: '' });
    };

    public decodeUrl = (urlParam: string) => {
        return urlParam.split('_').join(' ');
    };

    public checkUrlParam = () => {
        const search = (<Location>root.location).search;
        if (!search) {
            return;
        }
        const urlParams = new URLSearchParams(search);
        const { hasMultipleShades, defaultSku } = this.product;
        const { shades, sizes } = defaultSku || {};
        if (hasMultipleShades) {
            const shadeName = urlParams.get(ProductFullOrderableViewController.shadeUrlParam);
            if (!shadeName && shades[0] && shades[0].name) {
                this.urlUpdateState(
                    ProductFullOrderableViewController.shadeUrlParam,
                    shades[0].name
                );
            } else if (shadeName) {
                const decodedShadeName = this.decodeUrl(shadeName);
                this.handleShadeSelect(decodedShadeName);
            }
        } else {
            const sizeLabel = urlParams.get(ProductFullOrderableViewController.sizeUrlParam);
            if (!sizeLabel && sizes[0] && sizes[0].value) {
                this.urlUpdateState(
                    ProductFullOrderableViewController.sizeUrlParam,
                    sizes[0].value
                );
            } else if (sizeLabel) {
                const decodedSizeLabel = this.decodeUrl(sizeLabel);
                this.handleSizeSelect(decodedSizeLabel);
            }
        }
    };

    @computed
    public get modelAndSmooshImages() {
        let modelAndSmooshImages: IAsset[] = [];
        const { selectedSku } = this;

        if (selectedSku) {
            if (selectedSku && selectedSku.modelImage && selectedSku.modelImage.src) {
                modelAndSmooshImages = [selectedSku.modelImage];
            }
            if (selectedSku && selectedSku.smooshImage && selectedSku.smooshImage.src) {
                modelAndSmooshImages = [...modelAndSmooshImages, selectedSku.smooshImage];
            }
        }

        return modelAndSmooshImages;
    }

    @computed
    public get smallImages() {
        // use large product images for carousel until we have small images
        return this.largeImages;
    }

    @computed
    public get vtoFoundationEnabled() {
        const features = this.config && this.config.features;

        return features && features.vtoFoundation && features.vtoFoundation.enabled;
    }

    @computed
    public get vtoMakeupEnabled() {
        const features = this.config && this.config.features;

        return features && features.vtoMakeup && features.vtoMakeup.enabled;
    }

    @computed
    public get vtoMakeupFeatureEnabled() {
        const vto = this.vtoFeature;
        const hasColorExperience = this.product.hasColorExperience;

        return vto && vto.makeup && vto.makeup.enabled && hasColorExperience;
    }

    @computed
    public get vtoFoundationFeatureEnabled() {
        const vto = this.vtoFeature;
        const hasFoundationExperience = this.product.hasFoundationExperience;

        return vto && vto.foundation && vto.foundation.enabled && hasFoundationExperience;
    }

    @computed
    public get vtoFeature() {
        return this.config && this.config.vtoFeature;
    }

    @computed
    public get engravingEnabled() {
        const engraving = this.config && this.config.engraving;

        return engraving && engraving.enabled;
    }

    @computed
    public get favoritesEnabled() {
        const features = this.config && this.config.features;

        return features && features.favorites && features.favorites.enabled;
    }

    @computed
    public get charLimit() {
        const shortDescription = this.config && this.config.shortDescription;

        return (
            shortDescription &&
            shortDescription.overrides &&
            shortDescription.overrides.productFull &&
            shortDescription.overrides.productFull.charLimit
        );
    }

    @computed
    public get shouldRenderReadMoreLink() {
        const { description } = this.product;
        const charLimit = this.charLimit;

        if (!charLimit) {
            return false;
        }

        const descriptionCharCount = description.replace(/ /g, '').length;

        return descriptionCharCount >= charLimit;
    }

    @computed
    public get productTruncatedDescription() {
        const { description } = this.product;
        const charLimit = this.charLimit;

        return charLimit ? truncateStringToNearestWord(description, charLimit) : description;
    }

    @computed
    public get stickyAddToBagVisibleClassName() {
        return this.showStickyAddToBag
            ? STICKY_CLASS_NAMES.SHOW_STICKY
            : STICKY_CLASS_NAMES.HIDE_STICKY;
    }

    @action
    public productUpdateQuantity = (quantity: number) => {
        this.product.setQuantity(quantity);
    };

    @action
    public setShowStickyAddToBag = (showStickyAddToBag: boolean) => {
        this.showStickyAddToBag = showStickyAddToBag;
    };

    @action
    public toggleProductImageSection = (hidden: boolean) => {
        this.hideProductImageSection = hidden;
    };

    @action
    public togglePerfectShades = (isEnabled: boolean) => {
        this.isPerfectShadeEnabled = isEnabled;
    };

    @action
    public toggleProductInfoPopup = () => {
        this.isProductInfoPopupOpen = !this.isProductInfoPopupOpen;
    };

    @action
    public setProductInfoPopupConfig = (payload: IProductInfoPopupPayload) => {
        this.productInfoPopupFieldType = payload.productInfoPopupField;
        this.productInfoPopupHeader = payload.productInfoPopupHeader;
        this.toggleProductInfoPopup();
    };

    @action
    public setCurrentTabId = (tabId: string) => {
        this.currentTabIndex = tabId;
    };

    @computed
    get currentTabId() {
        return this.currentTabIndex;
    }

    @computed
    public get productDetails(): IProductDetails {
        const {
            whoItsFor: { content: whoItsFor },
            greatToKnow: { content: greatToKnow },
            whatItIs: { content: whatItIs },
            whatItDoes: { content: whatItDoes },
            keyIngredient,
            howDoesItWork: { content: howDoesItWork },
            howToUse: { content: howToUse }
        } = this.product;
        const allIngredients = buildIngredientsContent(this.selectedSku);

        return {
            whoItsFor,
            greatToKnow,
            whatItIs,
            whatItDoes,
            keyIngredient,
            allIngredients,
            howDoesItWork,
            howToUse
        };
    }

    @computed
    private get productInfoPopupData() {
        const details = this.productDetails;
        if (this.config && this.config.productInfoPopupConfig) {
            const { productInfoPopupField } = this.config.productInfoPopupConfig;
            if (productInfoPopupField) {
                return details[<ProductInfoPopupFieldType>productInfoPopupField];
            }
        }
        if (this.productInfoPopupFieldType) {
            return details[<ProductInfoPopupFieldType>this.productInfoPopupFieldType];
        }
    }

    @action
    public toggleDescription = () => {
        this.isDescriptionExpanded = !this.isDescriptionExpanded;
    };
}
